import React, { FunctionComponent, useEffect, useState } from 'react'

import BlockState from './BlockState'
import ModifyComponent from './call-to-action/ModifyComponent'
import Block from '../../../domain/Block/Block'

import FormGateway from '../../../gateway/Form/FormGateway'
import FindUseCase from '../../../domain/Form/FindUseCase'
import FetchElementGateway from '../../../gateway/Element/FetchElementGateway'
import ElementsPreviewPresenter from '../../../domain/Element/ElementsPreviewPresenter'
import ElementPreview from '../../../domain/Element/ElementPreview'

import parse from 'html-react-parser';
import envVariable from "../../util/envVariable";
import storage from '../../util/storage';
import { roleBackOfficeList } from '../../../domain/User/Erole'
import ReferentialGateway from '../../../domain/Referential/ReferentialGateway'
import GetReferential from '../../../useCase/Referential/GetReferential'
import { useFolderId } from '../../../infrastructure/hooks'
import { splitBlockCss } from '../../util/blockCssClasses'
import { EFolderStatus } from '../../../domain/Folder/EFolderStatus'

type Props = {
    pageId: string,
    block: Block,
    state: string,
    role: string
}

const BlockPreviewComponent: FunctionComponent<Props> = ({ pageId, block, state, role }) => {
  const { folderId } = useFolderId()
  const stateClass = BlockState.getStateClass(state)
  const previewClasses = splitBlockCss(block.cssClasses).preview
  // Le repli/dépli n'a de sens que sur un bloc complété (seul `card--completed`
  // porte le style de collapse) -> on n'expose le déclencheur que dans ce cas.
  const isCollapsible = previewClasses.split(/\s+/).includes('card--collapsible') && state === 'complete'
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  // L'ouverture au survol (et sa tempo anti-flickering) est gérée en pur CSS.
  // Côté JS on ne garde que la fermeture en quittant la zone : on dé-épingle un
  // bloc ouvert au clic et on retire le focus du bouton, sinon `:focus-within`
  // le maintiendrait ouvert. Scopé à un élément interne car le clavier ne
  // déclenche pas `mouseleave` -> son confort focus-within est préservé.
  const handleMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsExpanded(false)
    const active = document.activeElement
    if (active instanceof HTMLElement && event.currentTarget.contains(active)) {
      active.blur()
    }
  }

  const contentId = `block-content-${block.id}`
  const [formData, setFormData] = useState<ElementPreview[] | null>([])
  const isBackOfficeUser = roleBackOfficeList.includes(role)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    let isMounted = true

    const refentialGateway = new ReferentialGateway()
    const getRefential = new GetReferential(refentialGateway)

    getRefential.execute(null, block.id, folderId).then((referential) => {
      if (!isMounted) return
      const customer = envVariable('REACT_APP_CUSTOMER')
      const productCode = new FormGateway(folderId).getValueForElementId(referential?.productId ?? '') ?? ''

      if (state !== 'disable') {
        const fetchElementGateway = new FetchElementGateway(folderId)
        fetchElementGateway.getElementsByBlockId(block.id, productCode, customer, signal)
          .then((elements) => {
            if (!isMounted) return
            const findUseCase = new FindUseCase(new FormGateway(folderId))
            findUseCase.execute().then((data) => {
              if (isMounted && data !== null) {
                setFormData(new ElementsPreviewPresenter().execute(elements, data))
              }
            })
          })
          .catch(error => {
            if (error.name !== 'AbortError') {
              console.error('Erreur lors du chargement des éléments', error)
            }
          })
      }
    })

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [pageId, block.id, state])


  const [stateAnimationClass, setStateAnimationClass] = useState<string>('slideDown')

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (block.state === 'validate' || block.state === 'complete') {
        setStateAnimationClass('slideDown')
      }
    }, 1)

    return () => clearTimeout(timeoutId)
  }, [block.state])

  const dossierState = storage.getDossierState(folderId)

  return (
    <div className={`${stateAnimationClass}`}>
      <div
        className={`card ${stateClass} ${previewClasses} ${isExpanded ? 'card--expanded' : ''} u-mxAuto`}
        onMouseLeave={isCollapsible ? handleMouseLeave : undefined}
      >
        <div className="card__first">
          <h3 className="card__title">
            {isCollapsible ? (
              <button
                type="button"
                className="button-reset card__toggle"
                aria-expanded={isExpanded}
                aria-controls={contentId}
                onClick={() => setIsExpanded(value => !value)}
              >
                {block.title}
              </button>
            ) : (
              block.title
            )}
          </h3>
          <div className="card__content flex-container" id={contentId}>
            <p className="col-md-12">
              {
                formData != null
                && formData.map(
                  (data, index) => {
                    return (
                      data.presentation
                      && <span key={index}>{parse(data.presentation)} <br/> </span>
                    )
                  }
                )
              }
            </p>
          </div>
        </div>
        {
          //role === 'bo' && <ValidationComponent blockId={block.id}/>
          (role === 'bo' || isBackOfficeUser ) && <ModifyComponent blockId={block.id} pageId={pageId} dossierState={dossierState}/>
        }
        {
          role !== 'bo' && !isBackOfficeUser && (dossierState === EFolderStatus.Ongoing || dossierState === EFolderStatus.Pre_signature_validation || dossierState === '') && <ModifyComponent blockId={block.id} pageId={pageId} dossierState={dossierState}/>
        }
      </div>
    </div>
  )
}

export default BlockPreviewComponent
